// `useChangePings(api, companyId)` (RT-01): keeps the company's screens live while it is
// mounted. Mounted once by the company layout (routes/c.$slug.tsx), so every mode under it —
// Scorecard, Reports, Setup lists, Home — refetches what it shows when another session writes.
// Against the in-memory fake there is no server to stream from: the hook does nothing and the
// screens keep their focus and 60 s refetch.
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type { Api } from "../api/client";
import { type ChangeSource, connectChangePings } from "./change-pings";

/** The stream URL of a company on the API at `baseUrl` (`/` = same origin). */
export function changesUrl(baseUrl: string, companyId: string): string {
  return `${baseUrl.replace(/\/$/, "")}/v1/companies/${encodeURIComponent(companyId)}/changes`;
}

export function useChangePings(
  api: Api,
  companyId: string,
  open?: (url: string) => ChangeSource,
): void {
  const queryClient = useQueryClient();
  const baseUrl = api.mode === "http" ? api.baseUrl : undefined;
  useEffect(() => {
    if (!companyId || baseUrl === undefined) return;
    if (!open && typeof EventSource === "undefined") return;
    return connectChangePings({
      url: changesUrl(baseUrl, companyId),
      companyId,
      queryClient,
      ...(open ? { open } : {}),
    });
  }, [baseUrl, companyId, queryClient, open]);
}
